import type { StateCreator } from "zustand";
import toast from "@/components/ui/toast";
import {
  DeleteConversation,
  GetConversations,
  type HeraldPreviousConversation,
  RenameConversation,
} from "@/data-access/herald-chat/chat-queries";
import { client } from "@/lib/urqlProvider";
import {
  FIRST_USER_TITLE_MAX_LEN,
  mergeFetchedConversationTitle,
  normalizeHeraldChatRowTitle,
} from "@/lib/utils/heraldChatUtils";
import type {
  ConversationModalSetState,
  FetchConversationsOptions,
  HeraldChatHistoryModalState,
  HeraldChatHistorySlice,
  OptimisticConversationInput,
  SetRenameConversationTargetInput,
} from "./herald-chat-history.slice";

export const CONVERSATIONS_FETCH_LIMIT = 25;

type ModalKind = "rename" | "delete";

type HeraldChatHistoryStoreCreator = StateCreator<
  HeraldChatHistorySlice,
  [["zustand/devtools", never]],
  [],
  HeraldChatHistorySlice
>;

type HeraldChatSet = Parameters<HeraldChatHistoryStoreCreator>[0];

const buildOptimisticConversationRow = (
  id: string,
  normalizedTitle: string,
): HeraldPreviousConversation => {
  const now = new Date().toISOString();
  return {
    id,
    title: normalizedTitle,
    messageCount: 1,
    createdAt: now,
    updatedAt: now,
  };
};

const modalUiInitial: HeraldChatHistoryModalState = {
  targetConversationId: null,
  targetConversationTitle: "",
  titleToBeRenamed: "",
  isRenameConversationModalOpen: false,
  isDeleteConversationModalOpen: false,
  isDeletingConversation: false,
};

const openModalTargetState = (
  conversationId: string,
  kind: ModalKind,
): Pick<
  HeraldChatHistoryModalState,
  | "targetConversationId"
  | "isRenameConversationModalOpen"
  | "isDeleteConversationModalOpen"
  | "isDeletingConversation"
> => ({
  targetConversationId: conversationId,
  isRenameConversationModalOpen: kind === "rename",
  isDeleteConversationModalOpen: kind === "delete",
  isDeletingConversation: false,
});

const resetModalUiState = () => ({ ...modalUiInitial });

const modalDevtools: Record<ModalKind, { open: string; close: string }> = {
  rename: {
    open: "[User] Herald chat history: Rename modal",
    close: "[User] Herald chat history: Rename modal closed",
  },
  delete: {
    open: "[User] Herald chat history: Delete modal",
    close: "[User] Herald chat history: Delete modal closed",
  },
};

const createSetConversationModal =
  (set: HeraldChatSet, kind: ModalKind) =>
  (next: ConversationModalSetState) => {
    const labels = modalDevtools[kind];
    if (next.open) {
      const snapshotTitle = normalizeHeraldChatRowTitle(next.title);
      set(
        {
          ...openModalTargetState(next.conversationId, kind),
          ...(kind === "delete" ? { titleToBeRenamed: "" } : {}),
          targetConversationTitle: snapshotTitle,
        },
        false,
        labels.open,
      );
    } else {
      set(resetModalUiState(), false, labels.close);
    }
  };

type HeraldChatHistoryActions = Pick<
  HeraldChatHistorySlice,
  | "setRenameConversationTarget"
  | "setTitleToBeRenamed"
  | "setRenameConversationModal"
  | "setDeleteConversationModal"
  | "resetConversationModalState"
  | "fetchAllPreviousConversations"
  | "fetchMorePreviousConversations"
  | "optimisticallyUpsertConversationAtTop"
  | "registerConversationFromFirstStream"
  | "clearSkipMessagesFetchUnlessViewing"
  | "removeConversationFromList"
  | "replacePendingConversationWithServerId"
  | "renameConversation"
  | "deleteConversation"
>;

export const createHeraldChatHistoryActions: StateCreator<
  HeraldChatHistorySlice,
  [["zustand/devtools", never]],
  [],
  HeraldChatHistoryActions
> = (set, get) => ({
  registerConversationFromFirstStream: ({ conversationId, title }) => {
    const row = buildOptimisticConversationRow(
      conversationId,
      normalizeHeraldChatRowTitle(title),
    );

    set(
      (state) => ({
        skipMessagesFetchForConversationId: conversationId,
        allPreviousConversations: [
          row,
          ...state.allPreviousConversations.filter(
            (conversation) => conversation.id !== conversationId,
          ),
        ],
      }),
      false,
      "[User] Herald chat history: Register first stream conversation",
    );
  },

  clearSkipMessagesFetchUnlessViewing: (activeConversationId) => {
    const skip = get().skipMessagesFetchForConversationId;
    if (skip == null) return;
    if (activeConversationId !== skip) {
      set(
        { skipMessagesFetchForConversationId: null },
        false,
        "[User] Herald chat history: Clear message-fetch skip on thread change",
      );
    }
  },

  setRenameConversationTarget: (input: SetRenameConversationTargetInput) =>
    set(
      {
        targetConversationId: input.targetConversationId,
        titleToBeRenamed: input.titleToBeRenamed,
      },
      false,
      "[User] Herald chat history: Set rename target",
    ),

  setTitleToBeRenamed: (titleToBeRenamed) =>
    set(
      { titleToBeRenamed },
      false,
      "[User] Herald chat history: Set title to rename",
    ),

  setRenameConversationModal: createSetConversationModal(set, "rename"),
  setDeleteConversationModal: createSetConversationModal(set, "delete"),

  resetConversationModalState: () =>
    set(resetModalUiState(), false, "[User] Herald chat history: Reset modals"),

  optimisticallyUpsertConversationAtTop: (
    input: OptimisticConversationInput,
  ) => {
    const normalizedTitle = normalizeHeraldChatRowTitle(input.title);

    set(
      (state) => {
        const existing = state.allPreviousConversations.find(
          (conversation) => conversation.id === input.id,
        );
        if (existing != null) {
          if (normalizeHeraldChatRowTitle(existing.title) === normalizedTitle) {
            return state;
          }
          return {
            allPreviousConversations: state.allPreviousConversations.map(
              (conversation) =>
                conversation.id === input.id
                  ? { ...conversation, title: normalizedTitle }
                  : conversation,
            ),
          };
        }

        const row = buildOptimisticConversationRow(input.id, normalizedTitle);
        return {
          allPreviousConversations: [row, ...state.allPreviousConversations],
        };
      },
      false,
      "[User] Herald chat history: Optimistic conversation upsert",
    );
  },

  removeConversationFromList: (conversationId) =>
    set(
      (state) => ({
        allPreviousConversations: state.allPreviousConversations.filter(
          (conversation) => conversation.id !== conversationId,
        ),
      }),
      false,
      "[User] Herald chat history: Remove conversation from list",
    ),

  replacePendingConversationWithServerId: (pendingConversationId, input) => {
    const row = buildOptimisticConversationRow(
      input.id,
      normalizeHeraldChatRowTitle(input.title),
    );

    set(
      (state) => ({
        allPreviousConversations: [
          row,
          ...state.allPreviousConversations.filter(
            (conversation) =>
              conversation.id !== input.id &&
              conversation.id !== pendingConversationId,
          ),
        ],
      }),
      false,
      "[User] Herald chat history: Replace pending conversation with server id",
    );
  },

  renameConversation: async () => {
    const state = get();
    const id = state.targetConversationId;
    if (!id || !state.isRenameConversationModalOpen) {
      toast.error("No conversation to rename.");
      return;
    }

    const trimmedTitle = state.titleToBeRenamed
      .trim()
      .slice(0, FIRST_USER_TITLE_MAX_LEN);
    if (!trimmedTitle) {
      toast.error("Title cannot be empty.");
      return;
    }

    const result = await client
      .mutation(RenameConversation, {
        id,
        title: trimmedTitle,
      })
      .toPromise();

    if (result.error) {
      toast.error(result.error.message ?? "Failed to rename conversation.");
      return;
    }

    const payload = result.data?.renameConversation;
    if (payload == null) {
      toast.error("Failed to rename conversation.");
      return;
    }

    if (payload.__typename === "OperationInfo") {
      const firstMessage = payload.messages[0];
      toast.error(firstMessage?.message ?? "Failed to rename conversation.");
      return;
    }

    if (payload.__typename !== "ConversationType") {
      return;
    }

    const updated = payload;
    const oldLabel = state.targetConversationTitle;

    set(
      (s) => ({
        allPreviousConversations: s.allPreviousConversations.map(
          (conversation) =>
            conversation.id === updated.id
              ? {
                  ...conversation,
                  title: updated.title,
                  messageCount: updated.messageCount,
                  createdAt: updated.createdAt,
                  updatedAt: updated.updatedAt,
                }
              : conversation,
        ),
        ...modalUiInitial,
      }),
      false,
      "[User] Herald chat history: Rename conversation",
    );

    const newLabel = normalizeHeraldChatRowTitle(updated.title);
    toast.success(`Renamed from "${oldLabel}" to "${newLabel}"`);
  },

  deleteConversation: async (): Promise<boolean> => {
    const state = get();
    const id = state.targetConversationId;
    if (!id || !state.isDeleteConversationModalOpen) {
      toast.error("No conversation to delete.");
      return false;
    }
    if (state.isDeletingConversation) {
      return false;
    }

    const deletedLabel = state.targetConversationTitle;

    set(
      { isDeletingConversation: true },
      false,
      "[User] Herald chat history: Delete conversation pending",
    );

    try {
      const result = await client
        .mutation(DeleteConversation, { id })
        .toPromise();

      if (result.error) {
        toast.error(result.error.message ?? "Failed to delete conversation.");
        return false;
      }

      const payload = result.data?.deleteConversation;
      if (payload == null) {
        toast.error("Failed to delete conversation.");
        return false;
      }

      if (payload.__typename === "OperationInfo") {
        const firstMessage = payload.messages[0];
        toast.error(firstMessage?.message ?? "Failed to delete conversation.");
        return false;
      }

      if (payload.__typename !== "SuccessResponse") {
        return false;
      }

      set(
        (s) => ({
          allPreviousConversations: s.allPreviousConversations.filter(
            (conversation) => conversation.id !== id,
          ),
          skipMessagesFetchForConversationId:
            s.skipMessagesFetchForConversationId === id
              ? null
              : s.skipMessagesFetchForConversationId,
          ...modalUiInitial,
        }),
        false,
        "[User] Herald chat history: Delete conversation",
      );

      toast.success(`Deleted "${deletedLabel}".`);
      return true;
    } finally {
      if (get().isDeletingConversation) {
        set(
          { isDeletingConversation: false },
          false,
          "[User] Herald chat history: Delete conversation end pending",
        );
      }
    }
  },

  fetchAllPreviousConversations: async (
    options?: FetchConversationsOptions,
  ) => {
    set(
      {
        isFetchingConversations: true,
        conversationsFetchError: null,
      },
      false,
      "[User] Herald chat history: Fetch conversations start",
    );

    try {
      const variables = {
        pagination: { limit: CONVERSATIONS_FETCH_LIMIT },
      };
      const result = await client
        .query(
          GetConversations,
          variables,
          options?.requestPolicy
            ? { requestPolicy: options.requestPolicy }
            : undefined,
        )
        .toPromise();

      if (result.error) {
        toast.error("Failed to load conversations.");
        set(
          {
            isFetchingConversations: false,
            conversationsFetchError:
              result.error.message ?? "Failed to load conversations",
          },
          false,
          "[User] Herald chat history: Fetch conversations error",
        );
        return;
      }

      const results = result.data?.conversations?.results ?? [];
      const totalCount = result.data?.conversations?.totalCount ?? 0;
      set(
        (state) => {
          const normalizedResults = results.map((row) => ({
            ...row,
            title: mergeFetchedConversationTitle(
              state.allPreviousConversations.find((c) => c.id === row.id)
                ?.title,
              row.title,
            ),
          }));
          const nextOffset = results.length;
          const paginationState = {
            conversationsTotalCount: totalCount,
            conversationsNextOffset: nextOffset,
            hasMoreConversations: nextOffset < totalCount,
            isFetchingConversations: false,
            conversationsFetchError: null,
          };
          const currentId = options?.activeConversationId ?? null;
          if (currentId == null) {
            return {
              allPreviousConversations: normalizedResults,
              ...paginationState,
            };
          }
          const serverHasActive = normalizedResults.some(
            (row) => row.id === currentId,
          );
          if (serverHasActive) {
            return {
              allPreviousConversations: normalizedResults,
              ...paginationState,
            };
          }
          // New thread can be missing from the list response briefly; keep the optimistic row.
          const localRow = state.allPreviousConversations.find(
            (row) => row.id === currentId,
          );
          if (localRow == null) {
            return {
              allPreviousConversations: normalizedResults,
              ...paginationState,
            };
          }
          return {
            allPreviousConversations: [
              {
                ...localRow,
                title: mergeFetchedConversationTitle(localRow.title, undefined),
              },
              ...normalizedResults.filter((row) => row.id !== currentId),
            ],
            ...paginationState,
          };
        },
        false,
        "[User] Herald chat history: Fetch conversations success",
      );
    } catch (error) {
      console.error("fetchAllPreviousConversations:", error);
      toast.error("An error occurred while loading conversations.");
      set(
        {
          isFetchingConversations: false,
          conversationsFetchError:
            error instanceof Error ? error.message : "Unknown error",
        },
        false,
        "[User] Herald chat history: Fetch conversations error",
      );
    }
  },

  fetchMorePreviousConversations: async () => {
    const state = get();
    if (
      state.isFetchingMoreConversations ||
      state.isFetchingConversations ||
      !state.hasMoreConversations
    ) {
      return;
    }

    set(
      { isFetchingMoreConversations: true },
      false,
      "[User] Herald chat history: Fetch more conversations start",
    );

    try {
      const result = await client
        .query(GetConversations, {
          pagination: {
            limit: CONVERSATIONS_FETCH_LIMIT,
            offset: state.conversationsNextOffset,
          },
        })
        .toPromise();

      if (result.error) {
        toast.error("Failed to load more conversations.");
        set(
          { isFetchingMoreConversations: false },
          false,
          "[User] Herald chat history: Fetch more conversations error",
        );
        return;
      }

      const results = result.data?.conversations?.results ?? [];
      const totalCount = result.data?.conversations?.totalCount ?? 0;
      set(
        (currentState) => {
          const existingIds = new Set(
            currentState.allPreviousConversations.map(
              (conversation) => conversation.id,
            ),
          );
          const appendedRows = results
            .filter((row) => !existingIds.has(row.id))
            .map((row) => ({
              ...row,
              title: normalizeHeraldChatRowTitle(row.title),
            }));
          const nextOffset =
            currentState.conversationsNextOffset + results.length;

          return {
            allPreviousConversations: [
              ...currentState.allPreviousConversations,
              ...appendedRows,
            ],
            conversationsTotalCount: totalCount,
            conversationsNextOffset: nextOffset,
            hasMoreConversations: nextOffset < totalCount,
            isFetchingMoreConversations: false,
          };
        },
        false,
        "[User] Herald chat history: Fetch more conversations success",
      );
    } catch (error) {
      console.error("fetchMorePreviousConversations:", error);
      toast.error("An error occurred while loading more conversations.");
      set(
        { isFetchingMoreConversations: false },
        false,
        "[User] Herald chat history: Fetch more conversations error",
      );
    }
  },
});
